import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useUserStore } from '@/stores/user';

/**
 * 路由配置
 * 所有路由页面采用懒加载动态导入，优化首屏性能
 * 登录页为公开路由，其他路由需要登录态
 *
 * meta.componentName：用于 keep-alive :include 匹配的组件名（必须与页面 defineOptions name 一致）
 *
 * 业务路由（出国留学申请全流程）：
 *   /student 学生管理（列表） → /student/detail 学生详情（档案/教育/语言/经历/家长关联 + 流程环节入口）
 *   选校建议 / 院校申请 / 合同 / 签证页面按模块逐步补充（入口在学生详情页聚合）
 */
const routes: RouteRecordRaw[] = [
    {
        path: '/login',
        name: 'Login',
        component: () => import('../views/Login.vue'),
        meta: {
            title: '登录',
            public: true,
        },
    },
    {
        path: '/',
        redirect: '/welcome',
    },
    {
        path: '/welcome',
        name: 'Welcome',
        component: () => import('../views/Welcome.vue'),
        meta: {
            title: '首页',
            componentName: 'Welcome',
        },
    },
    {
        path: '/student',
        name: 'StudentList',
        component: () => import('../views/business/StudentList.vue'),
        meta: {
            title: '学生档案',
            componentName: 'BusinessStudent',
        },
    },
    {
        path: '/student/detail',
        name: 'StudentDetail',
        component: () => import('../views/business/StudentDetail.vue'),
        meta: {
            title: '学生详情',
            componentName: 'BusinessStudentDetail',
        },
    },
    {
        path: '/news',
        name: 'NewsList',
        component: () => import('../views/business/NewsList.vue'),
        meta: {
            title: '新闻动态',
            componentName: 'BusinessNews',
        },
    },
    {
        path: '/system/users',
        name: 'UserList',
        component: () => import('../views/system/UserList.vue'),
        meta: {
            title: '用户管理',
            componentName: 'SystemUser',
        },
    },
    {
        path: '/system/roles',
        name: 'RoleList',
        component: () => import('../views/system/RoleList.vue'),
        meta: {
            title: '角色管理',
            componentName: 'SystemRole',
        },
    },
    {
        path: '/system/menus',
        name: 'MenuList',
        component: () => import('../views/system/MenuList.vue'),
        meta: {
            title: '菜单管理',
            componentName: 'SystemMenu',
        },
    },
    {
        path: '/system/contract-template',
        name: 'ContractTemplate',
        component: () => import('../views/system/ContractTemplate.vue'),
        meta: {
            title: '合同模板',
            componentName: 'ContractTemplate',
        },
    },
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

/**
 * 全局路由守卫
 * 1. 设置页面标题
 * 2. 登录鉴权：未登录访问非公开路由时跳转登录页（携带 redirect 回跳参数）
 * 3. 菜单权限：已登录但无权限的路由给出提示并留在当前页
 */
router.beforeEach((to, _from, next) => {
    const title = to.meta['title'] as string | undefined;
    if (title) {
        document.title = `${title} - 卓越睿嘉留学申请管理系统`;
    }

    const userStore = useUserStore();
    const isPublic = to.meta['public'] === true;

    // 公开路由直接放行
    if (isPublic) {
        next();
        return;
    }

    // 未登录跳转登录页
    if (!userStore.isLoggedIn) {
        next({ path: '/login', query: { redirect: to.fullPath } });
        return;
    }

    // 菜单权限校验（首页固定放行）
    if (to.path !== '/welcome' && !userStore.canAccess(to.path)) {
        next(false);
        return;
    }

    next();
});

export default router;
